import { MouseEventHandler } from 'react';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import { List } from 'immutable';

import { androidExitApp, iosExitApp } from 'flow/actions/embedded-app-actions';
import Page from 'flow/components/Setting/Page';
import Button from 'flow/components/ui-components/Button';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { Calendar } from 'flow/types/Calendar';
import EmbeddedType from 'flow/types/embedded-type';
import {
    ChannelKey,
    ContactKey,
    ExtensionKey,
    QueueKey,
    ReferKey,
} from 'flow/types/EntityKeys';
import { Profile } from 'flow/types/Profile';
import { routes } from 'flow/types/routes';
import { TypedMap } from 'flow/types/TypedMap';
import { isExtensionKey } from 'flow/utils';
import CalendarEvents from './CalendarEvents';
import CalendarExternal from './CalendarExternal';
import CalendarExternalURL from './CalendarExternalURL';
import CalendarMainView from './CalendarMainView';

interface Props {
    calendar: TypedMap<Calendar>;
    entityKey: ChannelKey | ContactKey | ExtensionKey | QueueKey | ReferKey;
    profiles: List<TypedMap<Profile>>;
    onClickBack?: MouseEventHandler;
    updateOpeningHours: (openingHours: object) => void;
    clearCalendar?: () => void;
    updateCalendar?: (calendarEvent: object) => void;
    showExternallyManagedInfo?: boolean;
}

const CalendarBase = ({
    calendar,
    entityKey,
    profiles,
    onClickBack,
    updateOpeningHours,
    clearCalendar,
    updateCalendar,
    showExternallyManagedInfo,
}: Props) => {
    const { i18next, embedded } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { openModal } = useModal();
    const match = useRouteMatch();

    const title = isExtensionKey(entityKey)
        ? i18next.t('settings.workhours')
        : i18next.t('pbx.opening_hours');

    const renderExternalURL = () => (
        <Page
            header={i18next.t('calendar.other.external.url')}
            onClickBack={() => history.push(`${match.url}/external`)}
        >
            <CalendarExternalURL
                calendar={calendar}
                clearCalendar={clearCalendar}
                updateCalendar={updateCalendar}
            />
        </Page>
    );

    const renderExternal = () => (
        <Page
            header={i18next.t('calendar.other.external')}
            onClickBack={() => history.push(match.url)}
        >
            <CalendarExternal
                calendar={calendar}
                updateCalendar={updateCalendar}
            />
        </Page>
    );

    const renderListView = () => {
        const onClickBackPage = () => {
            if (
                match.path === routes['pbx.queues.entityKey.settings.calendar']
            ) {
                if (embedded === EmbeddedType.IOS) {
                    return dispatch(iosExitApp());
                }
                if (embedded === EmbeddedType.ANDROID) {
                    return dispatch(androidExitApp());
                }
            }
            return history.push(match.url);
        };

        return (
            <Page
                header={i18next.t('calendar.other.events')}
                onClickBack={onClickBackPage}
                action={
                    calendar.get('editable') ? (
                        <Button
                            color="primary"
                            iconKey="add"
                            className="py-1"
                            onClick={() =>
                                openModal('createCalendarEvent', { entityKey })
                            }
                            hideTextOnOnePanel={true}
                            aria-label={i18next.t('calendar.add.event')}
                        >
                            {i18next.t('calendar.add.event')}
                        </Button>
                    ) : null
                }
            >
                <CalendarEvents entityKey={entityKey} calendar={calendar} />
            </Page>
        );
    };

    const renderBase = () => {
        const onClickBackPage: MouseEventHandler = onClickBack
            ? (event) => {
                  if (
                      match.path ===
                      routes['extensions.entityKey.contact.calendar']
                  ) {
                      if (embedded === EmbeddedType.IOS) {
                          return dispatch(iosExitApp());
                      } else if (embedded === EmbeddedType.ANDROID) {
                          return dispatch(androidExitApp());
                      }
                  }
                  return onClickBack(event);
              }
            : null;

        return (
            <Page header={title} onClickBack={onClickBackPage}>
                <CalendarMainView
                    calendar={calendar}
                    profiles={profiles}
                    entityKey={entityKey}
                    showExternallyManagedInfo={showExternallyManagedInfo}
                    handleUpdateOpeningHours={updateOpeningHours}
                    handleClearCalendar={clearCalendar}
                />
            </Page>
        );
    };

    return (
        <Switch>
            <Route
                path={`${match.path}/external/url`}
                render={renderExternalURL}
            />
            <Route path={`${match.path}/external`} render={renderExternal} />

            <Route path={`${match.path}/events`} render={renderListView} />

            <Route path={match.path} render={renderBase} />
        </Switch>
    );
};

export default CalendarBase;
